import { rankSuitToCardIndex } from "../../theme/cards";
import type {
  ActionView,
  AwardView,
  CardView,
  LegalActionSetView,
  PlayerLeftReason,
  PotTier,
  SeatOwnerSlot,
  SeatOwnerView,
  SeatStateView,
  SeedRoundView,
  SpectatorView,
  StageView,
  TableDissolvedCause,
  WsEvent,
  WsEventKind,
} from "../../types/wire";

/**
 * Most recent host-ownership transfer (ADR-007 v1.3).
 * Populated by `applyHostOwnershipTransferred` on every wire
 * frame; the App-level fade-out clears the field a few seconds
 * later so the transient toast banner unmounts. Carries the
 * `server_unix_ms` anchor + `client_received_ms` snapshot so the
 * AFK countdown for the new host can project the 60-second
 * deadline into the local clock domain via the
 * `lib/timer_clock.ts` helpers (same skew-anchor pattern the
 * action-timer overlay uses).
 */
export interface HostTransferEvent {
  readonly transfer_seq: number;
  readonly old_host_seat: number | null;
  readonly new_host_seat: number;
  readonly reason: "afk_timeout" | "busted";
  readonly server_unix_ms: number;
  readonly client_received_ms: number;
}

/**
 * Active action-timer mirror (ADR-007 v1.1). Populated
 * by `applyTimerArmed` and cleared by `applyTimerExpired` + every
 * fresh-hand boundary. `client_received_ms` is the local wall
 * clock at WS arrival — paired with `server_unix_ms` it lets
 * `lib/timer_clock.ts::computeEffectiveDeadlineLocalMs` project
 * the deadline into the client's `Date.now()` domain so the
 * CountdownBar ticks smoothly even under clock skew.
 */
export interface ActiveTimer {
  readonly seat: number;
  readonly deadline_unix_ms: number;
  readonly server_unix_ms: number;
  readonly client_received_ms: number;
  /** Base-window action deadline (~25s, `action_deadline_unix_ms`
   *  on the wire). The seat-ring timer drains over this window
   *  when present so the primary read reflects the base budget,
   *  while `deadline_unix_ms` stays the 85s enforcement boundary
   *  that backs the secondary time-bank reserve cue. `undefined`
   *  on a pre-cutover server (or when the BE serde-defaulted the
   *  field to a degenerate `0`) — the reducer's sanity guard only
   *  stores a value strictly after `server_unix_ms`, so the
   *  projection falls back to the 85s window in that case. */
  readonly action_deadline_unix_ms?: number;
  /** Wall-clock millis at which the window was armed (wire field
   *  `armed_at_unix_ms`). The ring measures against
   *  `action_deadline − armed_at`, which is frame-independent;
   *  `undefined` on a pre-bump server falls back to the frame-derived
   *  length. */
  readonly armed_at_unix_ms?: number;
}

/**
 * Last-fired timer expiry mirror — surfaces the transient "시간
 * 초과" badge on the seat so other players see the auto-action
 * (auto-fold / auto-check) immediately instead of waiting for the
 * eventual `ActionApplied` frame. Auto-cleared by an App-level
 * 1.5-second fade-out effect; the receiving reducer just stamps
 * the latest expiry. `action` mirrors the wire payload so a fork
 * could render which auto-action was chosen.
 */
export interface LastTimerExpired {
  readonly seat: number;
  readonly action: ActionView;
  readonly expired_at_ms: number;
}

/**
 * Per-seat showdown reveal mirror — keyed in the store by `seat_idx`.
 * `hole_cards` is `null` for folded seats (post-fold concealment
 * contract per ADR-007 v0.9); for non-folded seats it carries the
 * two CardView pair the wire delivered. `hand_rank` is the final
 * `HandRank::category_label` ("Flush" / "Two Pair" / etc.) for
 * non-folded seats, empty string for folded seats.
 */
export interface ShowdownReveal {
  readonly hole_cards: readonly [CardView, CardView] | null;
  readonly hand_rank: string;
  /** Wire minor 42 — the ranks the hand is NAMED by. `hand_rank` is
   *  the category alone, which names every set of trips the same; this
   *  is what lets a surface say "5 트리플" rather than "트리플".
   *  `undefined` from a pre-bump server. */
  readonly hand_rank_ranks?: ReadonlyArray<number>;
}

/**
 * Per-seat row mirrored from `SeatStartedView` / `SeatSnapshotView`.
 * Kept mutable in the store under a fresh map produced by every
 * `apply_*` action; consumers must not mutate the rows in place
 * (Zustand snapshots are shallow-frozen in dev).
 */
export interface SeatRow {
  idx: number;
  state: SeatStateView;
  stack: number;
  committed_round: number;
  committed_hand: number;
  acted: boolean;
  /** Seat-owner 32-byte ed25519 vk (lowercase hex) threaded from the
   *  wire `SeatStartedView.owner` / `SeatSnapshotView.owner` field
   *  (wire minor 31). `undefined` on a pre-bump server frame that
   *  omits the owner — the renderer falls back to the persistent
   *  `roster` map's row + a short-hex label in that case. */
  ownerHex?: string;
  /** Seat-owner display name resolved server-side, or `null` when
   *  the accounts lookup missed (short-hex fallback). `undefined`
   *  when the wire omitted the owner entirely. */
  ownerName?: string | null;
  /** The seat sat down while this hand was running and plays from the
   *  next one (wire minor 39). Both an arriving seat and an absent one
   *  read `sitting_out`, and they want opposite words — labelling an
   *  arrival "자리비움" says the newcomer has stepped away. Written by
   *  `applyPlayerJoined` from the join frame and by both Snapshot
   *  reducers from the server's per-seat marker, so a reconnect into
   *  the same hand still reads the seat correctly. Cleared at every
   *  fresh-hand boundary, where the seat is genuinely in play. */
  waitingFirstHand?: boolean;
}

/**
 * Persistent seat→player membership entry (wire minor 31). Mirrors
 * the wire `SeatOwnerView` / `SeatOwnerSlot` shape. Lives in the
 * store's `roster` map — see the `GameState.roster` doc for the
 * single-writer reducer contract and the hand:None survival rule.
 */
export interface RosterEntry {
  /** The seat's chips, as of the frame that built this row. The roster
   *  is what the felt reads BETWEEN hands, when the hand-scoped `seats`
   *  map is empty — so this is the only stack a client has at a table
   *  that has not dealt yet. `0` from a pre-bump server, which is the
   *  same nothing the fabricated placeholder used to show. */
  readonly stack: number;
  /** 32-byte ed25519 vk, lowercase hex. */
  readonly playerIdHex: string;
  /** Resolved display name, or `null` when the accounts lookup
   *  missed (the renderer falls back to a shortened hex slice). */
  readonly username: string | null;
  /** The seat is sitting out (wire minor 43).
   *
   *  The same argument as `stack`, one field later. The felt reads
   *  this roster between hands, and with no state on it the seat rows
   *  were built with a hard-coded `"active"` — so a benched player was
   *  drawn as playing, told the shuffle was about to start, and left
   *  waiting for a hand that was waiting for them. `false` from a
   *  pre-bump server, which is the value that was fabricated anyway. */
  readonly sittingOut: boolean;
  /** The seat sat down mid-hand and plays from the next one (wire
   *  minor 43). An arriving seat and an absent one are both sitting
   *  out and want opposite words. */
  readonly waitingFirstHand: boolean;
}

/**
 * Coarse client-side state machine — drives the top-level UI between
 * "we're looking at a lobby", "we're walking through the seed-round
 * 4-phase dance", "a hand is live", "the showdown reveal is on screen",
 * and "the hand just settled".
 *
 * The `showdown` phase (ADR-007 v0.9) is the post-Settled reveal
 * window — the engine emits `Settled` first, then `HandShowdown` on
 * the showdown path, so the reducer overwrite order is
 * `phase: 'settled' → 'showdown'`. A 5-second App-level dwell timer
 * calls `endShowdownDwell()` to flip `showdown → settled`, which
 * surfaces the SettledSummary banner. On the `AllFolded` immediate-
 * win path no `HandShowdown` arrives at all, so the table never
 * leaves `settled` and the summary surfaces immediately.
 *
 * Transitions are driven exclusively by WsEvent arrivals + the
 * caller's user actions + the showdown dwell timer; no other timer-
 * based transitions live here.
 */
export type Phase =
  | "lobby"
  | "hand_active"
  | "showdown"
  | "settled"
  // Wire minor 21 — terminal lifecycle phase. Reached on a
  // `TableDissolved` reducer dispatch. No further wire frame
  // arrives after this one (the per-table broadcast channel
  // already dropped on the BE side); the App-level effect
  // surfaces the dismiss modal + drives the auto-redirect-to-
  // lobby CTA.
  | "dissolved"
  // Wire minor 22 — operator-issued single-seat kick targeted
  // at THIS client. Reached when `applyPlayerKicked` finds the
  // kicked seat matches the caller's own seat (read in-set from
  // the auth slice at dispatch time via `deriveMySeat`). Other
  // clients' kick frames don't reach this phase — they drop the
  // kicked seat from their roster + surface a notification
  // toast instead, while the table continues. The App-level
  // effect mounts the `KickedModal` on this phase + drives the
  // same auto-redirect-to-lobby CTA the dissolve modal uses.
  | "kicked"
  // Wire minor 45 — the server reclaimed THIS client's seat because
  // it had nothing left to play with. A disconnect sweep only ever
  // reaches somebody who has already closed the tab; this one lands
  // in front of the reader, so their chair must not simply disappear
  // from under them. Other seats' reclaims never reach this phase —
  // they drop from the roster like any other leave.
  | "seat_reclaimed";

/**
 * Typed table/hand lifecycle machine — the canonical source of
 * truth the WS reducers write, replacing the flat `Phase` string
 * as the WRITTEN field. `phase` becomes a PURE DERIVATION of this
 * union (`deriveLegacyPhase`) so every existing consumer that
 * string-compares `game.phase` keeps working byte-identically with
 * no migration — the seed-overlay idle gate, the action-bar
 * `mySeat !== null && phase === "hand_active"` gate, the roster
 * reducers' `liveHand` guard, the SettledSummary recap, and the
 * terminal redirect modals all read the derived `phase` exactly as
 * before.
 *
 * Each variant carries only the data its lifecycle stage legally
 * implies, so combinations the flat enum permitted but that the
 * engine never produces become structurally unrepresentable:
 *
 *   - `in_hand` is constructed solely by `applyHandStarted` /
 *     `applySnapshot { hand: Some }` / the spectator-lane mirrors,
 *     each of which requires the hand payload (seats + a final_seed
 *     or spectator strip) — so "a live hand on an empty felt" cannot
 *     exist. It carries the live `street` + `toAct`; `settled` /
 *     `showdown` carry no `toAct`, so "settled with a live actor" is
 *     unrepresentable.
 *   - `showdown` is reachable ONLY from `settled` (the
 *     `HandShowdown`-after-`Settled` engine order) — there is no edge
 *     from `lobby` / `in_hand` straight to `showdown`, so "showdown
 *     with no prior settled hand" cannot be constructed.
 *   - `dissolved` / `kicked` are ABSORBING — every non-`reset()` edge
 *     out of them is a no-op, so a race frame can never dismiss the
 *     terminal redirect modal. This is ENFORCED at the single
 *     `applyWsEvent` dispatch point (store.ts): a frame arriving while
 *     `TERMINAL_KINDS.has(table_state.kind)` is dropped (only the
 *     connection freshness stamp is kept), and `reset()` is the sole
 *     exit. A new terminal variant is covered automatically by adding
 *     its kind to `TERMINAL_KINDS`.
 *
 * The dead `joining` / `seed_round` flat values are dropped entirely
 * (no reducer ever wrote them; the seed-round dance is represented by
 * the `lobby` variant gated by the seed-projection trio +
 * `seed_round_pending`).
 */
export type TableState =
  | {
      /** Inter-hand pause / pre-first-hand idle. Maps to the legacy
       *  `lobby` phase. The seed-round 4-phase dance lives here too,
       *  surfaced by the seed-overlay selector over the
       *  `seed_in_flight` / `seed_round_pending` projection trio (this
       *  variant carries no seed data of its own). */
      readonly kind: "lobby";
    }
  | {
      /** A hand is live. Constructed only from a hand payload
       *  (`HandStarted` / `Snapshot { hand: Some }` / spectator
       *  mirrors), so the non-empty-seats invariant holds by
       *  construction. Maps to the legacy `hand_active` phase. */
      readonly kind: "in_hand";
      /** Live betting street — feeds the existing `stage` field
       *  (already derived from the same wire payload). */
      readonly street: StageView;
      /** Seat currently on the clock, or `null` between actors —
       *  feeds the existing `current_to_act` field. */
      readonly toAct: number | null;
    }
  | {
      /** Post-`Settled` reveal window (ADR-007 v0.9). Reachable ONLY
       *  from `settled` via `HandShowdown`, so it always implies a
       *  hand was started + settled. Maps to the legacy `showdown`
       *  phase; carries no `toAct` (the hand has resolved). */
      readonly kind: "showdown";
    }
  | {
      /** Hand resolved — awards posted, `current_to_act` forced
       *  null. Maps to the legacy `settled` phase. Carries no
       *  `toAct`, so a live actor cannot coexist with a settled
       *  hand. */
      readonly kind: "settled";
    }
  | {
      /** Wire minor 21 — terminal `TableDissolved`. Absorbing: every
       *  non-`reset()` edge out is a no-op so a race frame can't
       *  dismiss the redirect modal. Maps to the legacy `dissolved`
       *  phase; the modal copy reads the parallel
       *  `last_table_dissolved` stamp. */
      readonly kind: "dissolved";
    }
  | {
      /** Wire minor 22 — terminal self-target `PlayerKicked`.
       *  Absorbing (same contract as `dissolved`). Maps to the legacy
       *  `kicked` phase; the modal copy reads the parallel
       *  `last_player_kicked` stamp. */
      readonly kind: "kicked";
    }
  | {
      /** Wire minor 45 — terminal self-target seat reclaim. The
       *  caller ran out of chips, did not top up, and the server
       *  stood their seat up. Absorbing (same contract as
       *  `dissolved` / `kicked`): the reader is no longer at this
       *  table, so no later frame about it may write the machine. */
      readonly kind: "seat_reclaimed";
    };

export type TableStateKind = TableState["kind"];

/** The absorbing terminal kinds — `dissolved` / `kicked`. The
 *  `applyWsEvent` dispatch guard drops every GAME-LIFECYCLE frame while
 *  the machine sits in one of these (only `reset()` escapes), so the
 *  terminal redirect / kick modal cannot be dismissed by a race
 *  frame. A future terminal variant is added here (and the
 *  `TableState` doc's ABSORBING claim stays 1:1 with the code). */
export const TERMINAL_KINDS: ReadonlySet<TableStateKind> =
  new Set<TableStateKind>(["dissolved", "kicked", "seat_reclaimed"]);

/** Connection-domain WS frames — orthogonal to the game-lifecycle
 *  state machine. These write ONLY `connection.disconnect` (never
 *  `table_state` / `phase` / `seats`), so they BYPASS the terminal
 *  absorbing guard in `applyWsEvent`: an account-wide `session_revoked`
 *  (or a `displaced`) that lands AFTER a `dissolved` / `kicked` terminal
 *  must still flip the disconnect signal — the logout cascade (revoke)
 *  and the surface-halt (displaced) are auth/connection facts the
 *  game-lifecycle terminal has no authority to swallow. Dropping them
 *  would strand an open socket on a live account whose session the
 *  server already invalidated. The terminal kinds the absorbing guard
 *  protects are game-lifecycle facts only. */
export const CONNECTION_DOMAIN_KINDS: ReadonlySet<WsEventKind> =
  new Set<WsEventKind>(["displaced", "session_revoked"]);

/** Pre-built singleton variants for the data-free kinds — the
 *  reducers spread these into the atomic `set` instead of allocating
 *  a fresh object each frame. `in_hand` is constructed inline (it
 *  carries the per-frame `street` + `toAct`). */
export const TABLE_STATE_LOBBY: TableState = { kind: "lobby" };
export const TABLE_STATE_SHOWDOWN: TableState = { kind: "showdown" };
export const TABLE_STATE_SETTLED: TableState = { kind: "settled" };
export const TABLE_STATE_DISSOLVED: TableState = { kind: "dissolved" };
export const TABLE_STATE_SEAT_RECLAIMED: TableState = {
  kind: "seat_reclaimed",
};

/**
 * Total, deterministic map from the table-machine variant to the
 * legacy `Phase` string. This derivation is LOAD-BEARING: the
 * seed-overlay idle gate + the action-bar gate compare `phase` by
 * string equality, so the map MUST emit exactly the six live phase
 * values byte-for-byte. Keyed by `TableStateKind` so a future variant
 * (or a renamed kind) is a TS compile error here rather than a silent
 * phase typo.
 */
const LEGACY_PHASE_BY_KIND: Record<TableStateKind, Phase> = {
  lobby: "lobby",
  in_hand: "hand_active",
  showdown: "showdown",
  settled: "settled",
  dissolved: "dissolved",
  kicked: "kicked",
  seat_reclaimed: "seat_reclaimed",
};

/** Derive the legacy `game.phase` string from the table machine.
 *  Called inside the SAME atomic `set` each reducer already performs,
 *  so the emitted phase is identical to today's literal for every
 *  frame. */
export function deriveLegacyPhase(tableState: TableState): Phase {
  return LEGACY_PHASE_BY_KIND[tableState.kind];
}

/** Refine a live `in_hand` machine's `street` / `toAct` from an
 *  intra-hand frame (`ActionApplied` / `StageAdvanced` /
 *  `RoundClosed`), preserving the same `(street, toAct)` the derived
 *  `stage` / `current_to_act` fields carry. A no-op (returns the
 *  state unchanged) when the table is NOT `in_hand` — these frames
 *  never synthesize a hand from `lobby` / `settled` / a terminal
 *  state, matching the `phase`-write-free behavior of these reducers
 *  today (they only touch `stage` / `current_to_act`, never `phase`,
 *  so the derived phase is unaffected either way). */
export function refineInHand(
  tableState: TableState,
  street: StageView,
  toAct: number | null,
): TableState {
  if (tableState.kind !== "in_hand") return tableState;
  return { kind: "in_hand", street, toAct };
}

/**
 * Membership-lane type — structurally identical to the auth slice's
 * `AuthMode`. The lane lives SOLELY in the auth slice (`auth.mode`,
 * derived from its `sessionState` union); the WS reducers read it
 * in-set at dispatch time via `deriveMode` for the defensive
 * `final_seed` strip, and consumers subscribe to `auth.mode` directly.
 * No game-slice mirror exists — the prior `game.mode` field + its
 * `setMode` effect were the two-store drift bridge, now retired.
 */
export type Mode = "player" | "spectator";

/**
 * Disconnect signal — a discriminated union over the THREE distinct
 * domain facts a torn connection can carry, replacing the prior
 * single `displaced: boolean` that conflated two of them and inverted
 * the BE's newer-wins semantics:
 *
 *   - `live` — the socket is connected / reconnecting normally. The
 *     default; restored by `markReconnected` on every fresh bind.
 *   - `surface_displaced` — a NEWER surface for the SAME account
 *     claimed this table's `(table_id, player_id)` singleton slot
 *     (wire `displaced` frame / HTTP 403 `session.superseded`). The
 *     correct response is to HALT THIS SURFACE ONLY (stop the WS +
 *     polling, surface the take-over modal) — the account, cookie,
 *     and IndexedDB row are untouched, so the user can take over from
 *     here or continue in the other tab. This NEVER routes through
 *     the logout cascade.
 *   - `session_revoked` — the account session itself was invalidated
 *     server-side (wire `session_revoked` frame). The correct
 *     response is the full logout cascade (server revoke + IDB clear
 *     + cross-tab fan-out). This is the ONLY signal that justifies
 *     `runLogoutSequence`.
 *
 * Each variant carries only its own domain's data, so the two
 * "torn" facts can never share a slot (the source of the inverted
 * cascade): a `surface_displaced` signal cannot be mistaken for a
 * `session_revoked` one at the type level.
 */
export type DisconnectSignal =
  | { readonly kind: "live" }
  | { readonly kind: "surface_displaced"; readonly reason: string }
  | {
      readonly kind: "session_revoked";
      readonly reason: string;
      readonly revoked_at_ms: number;
    };

export const DISCONNECT_LIVE: DisconnectSignal = { kind: "live" };

/**
 * Connection lifecycle marker. `ws_connected = false` flips the HUD
 * into a "(연결 끊김)" banner; `last_event_ms` lets the staleness
 * selector + watchdog (`store/selectors.ts` `isStale` +
 * `session/ws_monitor.ts`) surface a "(데이터 오래됨)" hint when the
 * stream falls quiet past `STALENESS_THRESHOLD_MS` (heartbeat-aware,
 * see the selector doc) while the socket is still open.
 */
export interface ConnectionState {
  ws_connected: boolean;
  last_event_ms: number | null;
  /** Disconnect signal — `live` while connected/reconnecting; flips
   *  to `surface_displaced` (this surface halt) or `session_revoked`
   *  (account revoke → logout) when the BE tears the socket down.
   *  The WS client's on-close handler reads `disconnect.kind !==
   *  "live"` to stop the reconnect loop; the Game-level handler
   *  switches on the kind to route surface-halt vs logout. Restored
   *  to `live` by `markReconnected` on every fresh bind. */
  disconnect: DisconnectSignal;
}

/**
 * Wire minor 28 — engine-side post-resolve reject capture. The BE
 * emits `HandError { hand_seq, reason }` when an `ActionApplied`
 * would breach a settle-invariant (chip-conservation, all-in math,
 * stack underflow). The reducer stamps the most-recent reject so
 * an App-level toast effect can surface a Korean message through
 * `lib/server_error.ts mapServerErrorToToast(reason)`. The field
 * is cleared by `clearLastHandError` once the toast has been
 * acknowledged + when the next `HandStarted` lands.
 */
export interface LastHandError {
  readonly hand_seq: number;
  readonly reason: string;
}

/**
 * Per-event entry the HUD's action-log drawer renders as one
 * "Saby raises to 20" line. Derived from `ActionApplied` by
 * `applyActionApplied` — the wire never carries this shape, so
 * the entry lives in store state, not wire types. `username` is
 * the post-resolve display name resolved from the WS `roster` map;
 * falls back to "좌석 N" via the formatter when the row hasn't
 * landed yet. `stage` mirrors
 * the engine stage at the moment the action applied so the
 * renderer can group by street if a future iteration adds
 * dividers.
 */
export interface ActionLogEntry {
  readonly hand_seq: number;
  readonly action_seq: number;
  readonly seat: number;
  readonly stage: StageView;
  readonly action: ActionView;
  /** Round bet AFTER the action (engine `ActionApplied.round_bet`
   *  mirror). Caller-side `call` / `check` lines surface the chip
   *  amount via this field; bet / raise lines use the per-action
   *  `amount` / `to` directly. */
  readonly round_bet_after: number;
  /** Round bet BEFORE the action — the standing level this action
   *  answered. `round_bet_after` is the engine's POST-apply value and
   *  the two agree only while the round stays open: a call that CLOSES
   *  the round advances the street, so the frame reports `0`. Anything
   *  that means "what was on the table" reads THIS. */
  readonly round_bet_before: number;
  /** The acting seat's OWN committed total for the round, after the
   *  action. What an all-in actually pushed, which is neither the
   *  standing level (a short shove is less) nor the post-apply round
   *  bet (zero once the round closes). */
  readonly committed_round_after: number;
  /** `true` when the engine emitted this action via the AFK auto-
   *  policy (TimerExpired → auto-fold / auto-check) rather than a
   *  voluntary submit (GAME_PROGRESSION_FLOW_AUDIT_UX.md G-5).
   *  Stamped by `applyActionApplied` when the prior `TimerExpired`
   *  payload matches the incoming seat + action kind. Drives the
   *  action-log drawer's "⏰ 시간 초과 자동 …" label variant so the
   *  reader can distinguish a timed-out fold from a deliberate one.
   *  Optional + `undefined` for voluntary actions so a pre-bump
   *  reducer (or a snapshot rehydrate that has no timer context)
   *  defaults cleanly to the voluntary branch. */
  readonly timeout_origin?: boolean;
}

export const ACTION_LOG_MAX = 60;
export const EMPTY_ACTION_LOG: ReadonlyArray<ActionLogEntry> = [];

/**
 * Frozen-in-amber projection of the just-completed hand. Captured
 * at the next-hand boundary (`applyNextHandStarted` /
 * `resetForNextHand`) immediately before the reducer wipes the
 * carryable fields so the new hand starts clean. Drives the
 * `LastHandOverlay` slot the HUD menu opens when the user wants
 * to see "what just happened" after the table already moved on
 * (`HAND_HISTORY_LOOKBACK_AUDIT` Gap 3).
 *
 * Pure projection — no wire data, no localStorage round-trip;
 * lives in store memory only. A reload wipes the snapshot
 * cleanly (a fresh session with no immediately-prior hand is the
 * "no data yet" branch the overlay renders). Future
 * `HAND-HISTORY-PERSISTENCE` (Gap 4) replaces this with a
 * backend-served list.
 */
export interface LastHandSnapshot {
  /** `hand_seq` of the captured hand. Mirrors what the
   *  `applySettled` reducer saw. */
  readonly hand_seq: number;
  /** Final 5-card community board (or shorter on `AllFolded`
   *  immediate-win paths where the run-out cut off early). */
  readonly board: ReadonlyArray<CardView>;
  /** Total pot at settle. */
  readonly pot: number;
  /** Aggregate rake carved off the settled pot (`0` when no
   *  rake or pre-rake-hook server). */
  readonly rake_amount: number;
  /** Side-pot tier breakdown. Empty when the wire didn't carry
   *  `pot_tiers` so the renderer falls back to a single pot
   *  display via `pot`. */
  readonly pot_tiers: ReadonlyArray<PotTier>;
  /** Per-seat award rows. Mirrors `last_awards` at settle. */
  readonly awards: ReadonlyArray<AwardView>;
  /** Per-event action log captured immediately before the
   *  next-hand reducer wipes the live `action_log`. Oldest →
   *  newest (matches the live log's order). */
  readonly action_log: ReadonlyArray<ActionLogEntry>;
  /** Per-seat showdown reveal map captured at settle. Empty for
   *  `AllFolded` immediate-win paths or when no showdown was
   *  reached. */
  readonly showdown_reveals: ReadonlyMap<number, ShowdownReveal>;
  /** Per-seat row snapshot at the moment of capture. The
   *  `committed_hand` / `stack` fields read post-Settled, so the
   *  overlay can derive `net = awarded - invested` without
   *  re-deriving from the wire frame. */
  readonly seats: ReadonlyMap<number, SeatRow>;
  /** The chairs dealt into this hand, as frozen at the deal. `seats`
   *  also holds whoever sat down while the result was showing — they
   *  were at the table, not in the hand. Empty when the deal's roster
   *  never arrived (pre-bump server), which reads as "not told". */
  readonly dealt_seats: ReadonlyArray<number>;
  /** Caller's own hole cards (0..51 deck indices) for the
   *  captured hand. `null` when the caller had no seat or
   *  hadn't received a final-seed-driven derive. */
  readonly my_hole_cards: readonly [number, number] | null;
  /** Wall-clock millisecond stamp at capture (used purely for a
   *  "방금 핸드" sub-label — not for any monotonic guarantee). */
  readonly captured_at_ms: number;
}

/**
 * Game state mirror. The five ADR-007 forbidden fields are
 * deliberately absent: any compile-time access would fail under TS
 * strict mode, locking down Layer 1 of the trustless boundary. Layer
 * 2 (runtime zod schema) is a deferred follow-up.
 */
export interface GameState {
  // ---- wire-derived ----
  hand_id: number;
  hand_seq: number;
  button: number;
  blinds_small: number;
  blinds_big: number;
  pot: number;
  round_bet: number;
  /** Engine's current min-raise floor for the in-flight betting
   *  round, mirrored from `ActionApplied.min_raise` /
   *  `Snapshot.min_raise` on the wire. Starts at the big-blind value
   *  on a fresh hand and grows by the most recent legal-raise delta
   *  after every accepted Bet / Raise / AllIn-that-counts-as-raise.
   *  Maintained as wire state for reconnect/snapshot fidelity; the
   *  action bar no longer derives any legality bound from it (stage 4
   *  made the server `legal_actions` surface the single authority —
   *  the slider floor + raise presets read `min_raise_to` off that
   *  surface, not this delta). On a pre-bump server snapshot that
   *  omits the field, the reducer falls back to `blinds_big`. */
  min_raise: number;
  stage: StageView;
  board: ReadonlyArray<CardView>;
  seats: ReadonlyMap<number, SeatRow>;
  current_to_act: number | null;
  actions_taken: number;
  settled: boolean;
  last_awards: ReadonlyArray<AwardView>;
  /** Wire minor 50 — who took chips from a CONTESTED pot at the last
   *  settle, as player hex. `null` means the server did not say (a
   *  pre-bump server, or no settle yet), which is NOT the same as
   *  "nobody won": the crown is simply not drawn rather than guessed
   *  from the money, because an uncalled bet coming back is an award
   *  and is not a win. Reset alongside `last_awards`. */
  last_winners: ReadonlyArray<string> | null;
  /** Side-pot tier breakdown for the most recent Settled (cash-game
   *  standard). Empty when the wire didn't carry `pot_tiers` (pre-
   *  bump server) so the SettledSummary renderer falls back to the
   *  single-pot display. Reset to `EMPTY_POT_TIERS` on every fresh-
   *  hand boundary alongside `last_awards`. */
  last_pot_tiers: ReadonlyArray<PotTier>;
  /** Aggregate rake (chips) carved off the most recent Settled
   *  pot. `0` when the wire didn't carry `rake_amount` (pre-rake-
   *  hook server) or the operator's strategy is `NoRakeStrategy`.
   *  Drives the rakeback accumulator in the profile-history seam
   *  — App.tsx threads this into `recordHand({ rakeAmount, ... })`
   *  so the rakeback dashboard reads the authoritative server-side
   *  rake instead of the local pot-based estimate. Reset to `0`
   *  on every fresh-hand boundary alongside `last_awards`. */
  last_rake_amount: number;
  /** Append-only running log of `ActionApplied` events for the
   *  current hand, oldest → newest. Surfaces in the HUD's action-
   *  log drawer (`"Saby raises to 20 / Amro calls 20 / ..."`) so
   *  a returning observer can read the recent betting history
   *  without rebuilding it from the seat states. Wiped on every
   *  fresh-hand boundary (`HandStarted` / `applyNextHandStarted` /
   *  snapshot rejoin) alongside `last_awards`. Capped at the most
   *  recent `ACTION_LOG_MAX` (60) entries so the array stays O(1)
   *  in memory even across long heads-up grinds. */
  action_log: ReadonlyArray<ActionLogEntry>;
  waiting: boolean;
  max_seats: number;
  /** Forward-compatible game-type discriminator for the FE
   *  `resolveGameView` seam (mirrors the BE `trait Game` /
   *  `TexasHoldem` selection). The wire carries no `game_type` field
   *  yet — the server constructs `TexasHoldem` directly — so this
   *  stays `undefined` and the route selector synthesizes the
   *  `"holdem"` default. No reducer writes it; it is an inert
   *  extension point until a second game lands a wire field. */
  game_type?: string;
  /** The table's maximum buy-in, served on `GET /tables/:id/state`
   *  beside `min_buy_in` (`max_buy_in`). It was already being parsed
   *  and then dropped, so the top-up dialog re-derived nothing at all
   *  and offered fixed amounts at every stake. `0` is "not hydrated";
   *  callers fall back to the blind-derived value. */
  max_buy_in: number;
  /** System-wide table buy-in floor (`MIN_BUY_IN_CHIPS = 1_000` in
   *  `arkhe-holdem-service::wallet`). Hydrated by `setMinBuyIn` from
   *  the same `GET /tables/:id/state` REST fetch that feeds the
   *  seat-owner map (the WS `Snapshot` frame does NOT carry this
   *  field — the BE serves it only on the REST state endpoint, wire
   *  minor 25). Defaults to `0`; the in-game seat-tap bounds treat a
   *  `0` value as "not yet hydrated" and fall back to
   *  `DEFAULT_MIN_BUY_IN_CHIPS` so the floor never drops below the
   *  server-enforced minimum (matching the lobby join-modal floor +
   *  the BE `buy_in_below_minimum` gate). */
  min_buy_in: number;
  /** 6-character invite code for the table the caller is seated
   *  at. Hydrated by `setInviteCode` from the same one-shot
   *  `GET /tables/:id/state` config read that feeds `max_seats` /
   *  `min_buy_in` (the WS wire carries no invite code). `""`
   *  until that read lands, on a pre-bump BE peer, or for a table
   *  whose code the server no longer caches — the HUD renders no
   *  invite chip in that case rather than an empty one. */
  invite_code: string;
  /** Server-authoritative legal-action surface for the caller's OWN
   *  turn — the SOLE legality source (BE-authoritative legal actions,
   *  stage 4). Delivered on EVERY turn-advance: the live broadcast
   *  fields `hand_started.initial_legal` / `action_applied` /
   *  `stage_advanced` / `round_closed.next_legal` (the per-turn live
   *  path), the player-lane `HandSnapshot.my_legal_actions` (reconnect),
   *  and the `POST /action` response `legal` (own-response). All carry
   *  the engine `HandState::legal_actions` authority's
   *  `LegalActionSetView` — the SAME bounds source the server validates
   *  `apply_*` against, so the offered Call / Bet / Raise bounds can
   *  never diverge from what the engine accepts. `null` when it is not
   *  the caller's turn: the reducers store it ONLY when the post-event
   *  `next_to_act === mySeat` (and the surface names this seat), else
   *  clear — so a stale surface from a prior turn can never gate the bar
   *  on a peer's clock. `fromServer` consumes it; when `null` on the
   *  caller's own turn the action bar FAILS CLOSED (no local
   *  derivation survives — that was deleted in stage 4). */
  legal_actions: LegalActionSetView | null;
  // ---- local-derived (NEVER on wire) ----
  /** 32-byte post-reveal aggregate parsed from `HandStarted` /
   *  `Snapshot { hand: Some(_) }` payload. Local-derive anchor for
   *  the caller's own hole cards via the arkhe-rand TS port. */
  /** Caller's hole cards (0..51 deck indices), populated post-derive
   *  by the arkhe-rand TS port. Lives here so the HUD can render
   *  without re-deriving on every paint. */
  my_hole_cards: readonly [number, number] | null;
  /** Per-seat starting stack captured at HandStarted / Snapshot —
   *  used by the settled-summary line to render the caller's net
   *  delta + start→end arc. */
  hand_start_stacks: ReadonlyMap<number, number>;
  /** Immutable, sorted set of seat indices the BE dealt over for the
   *  CURRENT hand — captured once at `HandStarted` (from `ev.seats`)
   *  and at a `Snapshot { hand: Some }` reconnect (from the immutable
   *  `hand.dealt_seats` wire field), and NEVER touched by the
   *  leave/kick reducers. The own-hole-card derive walks this roster
   *  (not the live, shrinkable `seats` map) so a mid-hand
   *  leave/admin-kick of another seat cannot shift the deal cursor and
   *  corrupt the caller's own pair. The cards become a pure function
   *  of `(final_seed, button, dealt_seats, max_seats)` fixed for the
   *  hand. Wiped to `[]` at every inter-hand boundary (the next
   *  `HandStarted` / `Snapshot` re-sets it fresh).
   *
   *  Reconnect-after-leave correctness: the server carries the
   *  hand-start roster on `Snapshot.hand.dealt_seats`, frozen at deal
   *  time and never shrunk by a mid-hand departure, so a reconnect
   *  AFTER a peer left still re-derives against the exact set the deal
   *  used. The reducer falls back to the live (shrinkable) snapshot
   *  roster only for a pre-bump server that omits the field. */
  dealt_seats: ReadonlyArray<number>;
  /** Caller's monotonic action sequence number (per `(table,
   *  player, hand_seq)` scope — strict-monotonic-within-a-hand
   *  semantics). Speculative-bumped via `bumpActionSeq()` before a
   *  submit, rolled back via `rollbackActionSeq()` if the POST fails,
   *  and reset to 0 via `resetActionSeq()` on (re)join — a same-seat
   *  re-take is a server-side no-op now, so the client simply lines up
   *  with a fresh sequence space. Additionally reset to 0 at every
   *  fresh-hand boundary (`applyNextHandStarted` / `resetForNextHand`)
   *  so the first outgoing envelope of the new hand carries
   *  `action_seq = 1`, matching the server's per-hand baseline: a
   *  brand-new `HandActionWindow` is constructed at every hand open
   *  (structural rebase — no reset call to forget), starting every
   *  player at `next_seq = 1`. Keeping the counter across hands left
   *  the client and server out of sync on rollback drift / page reload
   *  / WAL replay races — the per-hand reset shrinks the divergence
   *  window to a single hand. */
  my_action_seq: bigint;
  /** Read-only observers attached to the current table, keyed by
   *  `player_id` (32-byte ed25519 vk hex). Mirrors the wire
   *  `SpectatorView` payload — `username` is the resolved display
   *  name or `null` when the accounts row could not be looked up. */
  spectators: ReadonlyMap<string, string | null>;
  /** Monotonic counter bumped by `resetForNextHand()` after a host
   *  `/tables/:id/next-hand` POST succeeds. The seed-orchestrator
   *  `useEffect` includes this in its dependency array so the
   *  effect re-fires for the new hand — without it the host's
   *  "다음 핸드 시작" click POSTs the next-hand bump but no client
   *  ever submits the next seed-round contribution, leaving the
   *  table in an indefinite "waiting on seed" state. Counter form
   *  is deliberate over keying off `phase` so re-fires align to
   *  the exact next-hand boundary instead of every phase
   *  transition. */
  next_hand_trigger: number;
  /** Monotonic counter bumped by `applyPlayerJoined` on every
   *  `PlayerJoined` WS frame the client accepts. App.tsx pulls the
   *  field into its seat-owner refetch effect's dependency array
   *  so a joiner-sat-down event triggers an immediate `GET
   *  /tables/:id/state` refresh — without it the seat-owner map
   *  stays stale until the next `HandStarted`, surfacing the
   *  "현재 참여 인원: 1명" bug after the second player joined.
   *  Counter form (rather than e.g. timestamp) gives a stable
   *  dependency identity so React's deps comparison fires exactly
   *  once per accepted frame even if multiple frames land in the
   *  same render tick. */
  player_joined_seq: number;
  /** Symmetric counterpart to `player_joined_seq` — bumped by
   *  `applyPlayerLeft` on every accepted `PlayerLeft` WS frame.
   *  Drives the same App-level seat-owner refetch effect so a
   *  voluntary-leave departure clears the seat roster row even
   *  before the next `HandStarted` rebuilds it from the actor
   *  array. Counter form keeps the deps identity stable across
   *  renders. */
  player_left_seq: number;
  /** Monotonic counter bumped by `applyLagged` on every
   *  `Lagged` WS frame the client accepts. tokio's broadcast
   *  channel surfaces a `Lagged(n)` notice when a slow subscriber's
   *  ring buffer overflowed — the FE responds by forcing a fresh
   *  WS reconnect so the server-side handshake replays a clean
   *  `Snapshot` frame, restoring the locally-stale state to the
   *  authoritative roster. App.tsx pulls the field into its WS
   *  subscription effect's dependency array so a Lagged event
   *  triggers exactly one teardown + reopen cycle. Counter form
   *  keeps the deps identity stable across renders. */
  lagged_seq: number;
  /** Current host seat (ADR-007 v1.3). `null` when
   *  no host is assigned (empty table just-created, or the host
   *  busted out without a transfer ready). **Table-lifetime**:
   *  the reducer carries this across every fresh-hand boundary
   *  — `applyHandStarted` / `applyNextHandStarted` /
   *  `resetForNextHand` / both Snapshot reducers must NOT wipe
   *  it (table-lifetime carry-not-wipe pattern, audit-locked).
   *  The only writers are `applyHostOwnershipTransferred`,
   *  the Snapshot{Some(_)} branches (when the wire field is
   *  present), and `reset()` (table-leave). */
  host_seat: number | null;
  /** Monotonic counter bumped by `applyHostOwnershipTransferred`
   *  on every accepted frame. Mirrors the wire's `transfer_seq`
   *  field so a downstream selector can detect a re-entry to the
   *  host role without watching the full event. */
  host_transfer_seq: number;
  /** Most recent host-ownership transfer for the transient toast
   *  banner + AFK countdown anchor. Auto-cleared by an App-level
   *  fade-out a few seconds after arrival. `null` between
   *  transfers + at table entry. */
  last_host_transfer: HostTransferEvent | null;
  /** Active action-timer for the seat currently on the clock, or
   *  `null` when no timer is armed. Populated by `applyTimerArmed`
   *  (which snapshots `client_received_ms = Date.now()` for the
   *  skew anchor) and cleared by `applyTimerExpired` + every
   *  fresh-hand boundary. The Seat / ActionBar components read
   *  this via the App-level wiring to render the CountdownBar
   *  overlay; only one timer is ever armed at a time per ADR-007
   *  v1.1 §TimerArmed invariant. */
  active_timer: ActiveTimer | null;
  /** Most recent expiry surface (auto-fold / auto-check). The
   *  store stamps this on `applyTimerExpired`; an App-level
   *  effect fades it out 1.5 s later by setting back to `null`.
   *  Other players see the transient badge without waiting for
   *  the eventual `ActionApplied` frame. */
  last_timer_expired: LastTimerExpired | null;
  /** Live seed-round projection (wire minor 18). `null` between rounds;
   *  populated by `applySeedRoundOpened` (fresh broadcast) + the
   *  Snapshot reducers (reconnect-time hydration from
   *  `Snapshot.seed_in_flight`). `applySeedContribLanded` /
   *  `applySeedRevealReady` mutate `contributed_player_ids` /
   *  `stage` in place across the auto-chain. Cleared on
   *  `applySeedFinalized` and on every fresh-hand boundary
   *  (`HandStarted` / `NextHandStarted` / `resetForNextHand`) so a
   *  stale round projection cannot linger past the
   *  `seed_finalized` → `hand_started` handoff. The
   *  orchestrator (`session/seed_orchestrator.ts`) reads this
   *  reactively to skip its now-removed polling loops. Mutual-
   *  exclusion priority: when `phase === 'hand_active'` /
   *  `'showdown'` / `'settled'`, the field is ignored by
   *  the overlay even if a future invariant-violating frame leaves
   *  it `Some`. */
  seed_in_flight: SeedRoundView | null;
  /** One-shot anchor stamped by `applySeedFinalized` carrying the
   *  hand_seq of the round that just finalized. The orchestrator
   *  reads this as its primary terminal trigger (HandStarted
   *  serves as the dual-trigger fallback when the PG persist
   *  await window stretches between the two frames). Cleared by
   *  every fresh-hand boundary alongside `seed_in_flight`. */
  last_seed_finalized_seq: number | null;
  /** Most recent stale-seed-sweep abort. Stamped by
   *  `applySeedRoundAborted` on every `seed_round_aborted` frame.
   *  Carries the hand_seq + the missing player_ids list +
   *  the abort reason so the orchestrator's transition guard can
   *  branch on own-sit-out vs other-sit-out + the App-level toast
   *  effect can surface a Korean cue. Cleared by every fresh-hand
   *  boundary so a stale stamp from the prior round can't
   *  contaminate the next overlay decision. */
  last_seed_round_aborted: {
    readonly hand_seq: number;
    readonly missing_player_ids: ReadonlyArray<string>;
    readonly reason: {
      readonly kind: "timeout_missing_contribs";
      readonly threshold_ms: number;
    };
  } | null;
  /** Pre-seed inter-hand waiting signal — the ONE canonical field
   *  the seed-overlay selector cannot otherwise derive. `true` means
   *  the table has entered the inter-hand window and a seed round for
   *  `hand_seq + 1` is expected to open; the host/joiner waiting
   *  spinner shows while `seed_in_flight === null` AND `phase ===
   *  'lobby'`. The orchestrator stamps NOTHING — the overlay phase
   *  has exactly one writer path (the selector over canonical state),
   *  and this flag is fed only by WS-frame reducers / the canonical
   *  next-hand path:
   *
   *    SET true — `applyNextHandStarted` (WS next_hand boundary) +
   *      `resetForNextHand` (host's local next-hand path); the
   *      inter-hand `Snapshot { hand: None }` branch when no seed
   *      round is already in flight (reconnect into the pre-seed
   *      window). Both next-hand paths also flip `phase → 'lobby'`
   *      and bump `next_hand_trigger`, so the flag SET is co-located
   *      with the phase flip (no follow-up effect / sub-frame gap).
   *
   *    CLEAR false — every fresh-hand / round-in-flight boundary
   *      alongside `seed_in_flight`: `applySeedRoundOpened`
   *      (round now in flight → the selector's contribute rule owns
   *      it), `applySeedFinalized`, `applySeedRoundAborted`,
   *      `applyHandStarted`, `applySnapshot { hand: Some }`, and
   *      `reset()`.
   *
   *  Mutual-exclusion: irrelevant whenever `seed_in_flight !== null`
   *  (the round-in-flight rule wins) or `phase` is a live/terminal
   *  hand phase (the idle rule wins); the selector evaluates those
   *  higher-priority rules first. */
  seed_round_pending: boolean;
  /** HTTP-terminal seed failure surface — the orchestrator's ONLY
   *  write into the game store, and a SETTER, not a phase write. A
   *  5xx / `commit_mismatch` / `unknown_player` contribute failure is
   *  NOT representable in the canonical seed projection, so the
   *  orchestrator stamps `{ hand_seq, message }` here via
   *  `stampSeedLocalError` on terminal failure and the overlay
   *  selector folds it into its error branch (gated on
   *  `hand_seq === game.hand_seq + 1` so a stale stamp from a prior
   *  round cannot re-show the error). This preserves the
   *  user-facing "카드 섞기 실패" toast while keeping exactly one
   *  writer of the overlay PHASE = the selector. Cleared on every
   *  fresh-hand / round boundary alongside `seed_in_flight` +
   *  `seed_round_pending`. */
  last_seed_local_error: {
    readonly hand_seq: number;
    readonly message: string;
  } | null;
  /** Wire minor 21 — most-recent `TableDissolved` payload. `null`
   *  until the BE publishes the lifecycle dismiss frame; populated
   *  by `applyTableDissolved` simultaneously with the `phase`
   *  flip to `'dissolved'`. The App-level effect reads this to
   *  drive the dismiss modal's cause-specific copy and the
   *  admin-supplied reason (when present). Terminal field — no
   *  reducer ever clears it short of a full store reset, since
   *  the channel that would carry a fresh frame has already
   *  closed on the BE side. */
  last_table_dissolved: {
    readonly cause: TableDissolvedCause;
    readonly dissolved_at_ms: number;
  } | null;
  /** Wire minor 24 — most-recent `PlayerLeft` payload. The
   *  reducer stamps this on every player-left broadcast (own OR
   *  other) so the App-level toast effect can surface a
   *  "포기 후 퇴장" cue when `forfeit_reason === "voluntary"` or a
   *  generic "퇴장" cue on the natural inter-hand path. `null`
   *  before any leave has landed; carry-not-wipe across hands so
   *  a late-mount overlay doesn't lose the most recent cue. */
  last_player_left: {
    readonly seat: number;
    readonly player_id: string;
    readonly username: string | null;
    readonly forfeit_reason: "voluntary" | null;
    /** Why the seat went vacant (wire minor 45). `null` from a
     *  pre-bump server. The reclaim arm (`"busted"`) is the one the
     *  client acts on — every other value leaves the existing silent
     *  seat removal untouched. */
    readonly reason: PlayerLeftReason | null;
  } | null;
  /** Wire minor 22 — most-recent `PlayerKicked` payload. Populated
   *  by `applyPlayerKicked` on every kick frame (self OR other).
   *  Self-kick paired path also flips `phase` to `'kicked'`. The
   *  App-level effect reads this on every change — when the
   *  payload names this client, the kick modal mounts; when it
   *  names another seat, a one-shot notification toast fires +
   *  the seat already dropped from the roster via the reducer's
   *  seats-map mutation. Cleared on the next fresh-hand boundary
   *  for the non-self path so a stale stamp doesn't re-fire the
   *  toast on every reducer re-run (self-kick keeps the stamp
   *  through the terminal `phase === "kicked"` window until the
   *  return-to-lobby CTA triggers a full store reset). */
  last_player_kicked: {
    readonly seat_idx: number;
    readonly player_id: string;
    readonly admin_account_id: number;
    readonly reason: string;
    readonly kicked_at_ms: number;
  } | null;
  /** Wire minor 23 — user-driven sit-out status per seat. Keyed
   *  by `seat_idx`; populated by `applySeatSitOutFlipped` on
   *  every `seat_sit_out_flipped` frame. `queued: true` means
   *  the flip is parked on the BE's `pending_sit_out` queue + will
   *  drain at the next-hand boundary; the HUD reads this to
   *  render the "다음 핸드 후 sit-out" badge variant. `sit_out:
   *  true + queued: false` means the flip already applied to the
   *  seat row (the wire's seat_state mirrors this). The map keeps
   *  entries indefinitely so a reconnecting Snapshot replay
   *  doesn't lose the queued cue — the next-hand boundary's
   *  `applyNextHandStarted` reducer drops entries whose seat now
   *  reads `sitting_out` (queued resolved). */
  seat_sit_out_status: ReadonlyMap<
    number,
    {
      readonly queued: boolean;
      readonly flipped_at_ms: number;
      readonly sit_out: boolean;
    }
  >;
  /** Per-seat showdown reveal map, populated by `applyHandShowdown`
   *  (ADR-007 v0.9). Keyed by `seat_idx`. Empty between hands and
   *  through every non-showdown settle (`AllFolded` immediate-win)
   *  — the reducer cleared on every fresh-hand boundary
   *  (`HandStarted` / `NextHandStarted` / `resetForNextHand()` /
   *  player+spectator `Snapshot` re-attach). The Showdown overlay
   *  reads this to render each non-folded seat's two hole cards
   *  face-up + the final `hand_rank` category label; SettledSummary
   *  reads it to surface the hand_rank next to each player row.
   *
   *  Voluntary post-settle reveals (`seat_revealed_hole`) also
   *  merge into this slot so a caller who hits "Show cards" after
   *  losing a showdown gets their two cards painted alongside the
   *  contested winner's. */
  showdown_reveals: ReadonlyMap<number, ShowdownReveal>;
  /** Seats that reached the showdown without revealing their hole
   *  cards (auto-muck losers under the PokerStars / GGPoker /
   *  partypoker / 888 default). Surfaced verbatim from the
   *  `settled` wire frame; cleared on every fresh-hand boundary.
   *  Empty on the `AllFolded` immediate-win settle (no showdown
   *  reached) + on every pre-bump settle where the BE didn't
   *  stamp the field. SettledSummary renders the "Mucked" badge
   *  per matching seat. */
  mucked_seats: ReadonlyArray<number>;
  /** Lookback snapshot of the *previous* hand
   *  (`HAND_HISTORY_LOOKBACK_AUDIT` Gap 3). Captured by
   *  `applyNextHandStarted` / `resetForNextHand` immediately
   *  before the carryable fields wipe, so the user can revisit
   *  "방금 끝난 핸드" after the new hand has already started.
   *  `null` on a fresh session (no immediately-prior hand) or
   *  after a non-settled phase transition (e.g. spectator
   *  re-attach mid-hand). Wiring layer surfaces a HUD menu
   *  CTA + the `LastHandOverlay` slot reads this directly. */
  last_hand_snapshot: LastHandSnapshot | null;
  // ---- runtime UX state ----
  /** Typed table/hand lifecycle machine — the canonical, SOLE-WRITTEN
   *  lifecycle field (the WS reducers + the showdown-dwell action +
   *  `reset()` are its only writers). See the `TableState` doc for
   *  the variant set + the structural invariants it enforces. */
  table_state: TableState;
  /** Legacy lifecycle string — now a PURE DERIVATION of `table_state`
   *  via `deriveLegacyPhase`, computed inside the same atomic `set`
   *  the reducer performs so it stays byte-identical to the value the
   *  reducers wrote literally before the machine landed. Every
   *  existing consumer reads this field unchanged; no reducer writes
   *  it directly anymore. */
  phase: Phase;
  connection: ConnectionState;
  /** Monotonic counter bumped by `takeOverSurface()` when the
   *  user taps the surface-displaced take-over CTA. The Game-level WS
   *  subscription effect includes this in its dependency array so a
   *  take-over tears down the halted socket + reopens — the fresh
   *  handshake re-claims the `(table_id, player_id)` singleton slot
   *  for THIS surface, displacing whatever newer surface bumped it.
   *  Counter form (not a boolean) keeps the deps identity stable and
   *  lets a repeated take-over re-fire the effect. */
  surface_retry_seq: number;
  /** Wire minor 28 — engine-side post-resolve reject capture. Set
   *  by the `HandError` reducer when the BE emits a settle-
   *  invariant breach + cleared by `clearLastHandError` or the
   *  next `HandStarted` cue. App-level toast effect surfaces a
   *  Korean message through `lib/server_error.ts
   *  mapServerErrorToToast(reason)`. `null` on a fresh session
   *  + after the engine recovers via the next hand boundary. */
  last_hand_error: LastHandError | null;
  /** Spectate-drain marker — `true` once a `PlayerLeft` frame drops
   *  the last seated row to zero (`applyPlayerLeft` stamps it when
   *  the resulting `seats` map is empty). Cleared by any reseating
   *  frame (`applyPlayerJoined` / `applyHandStarted` /
   *  `applyNextHandStarted` / both Snapshot reducers) + `reset()`.
   *  The Game spectator view reads this (gated on `isSpectator` +
   *  no live hand) to surface the "테이블이 비었습니다" notice +
   *  a return-to-lobby affordance — the queue removal means a
   *  spectator on a fully-drained table would otherwise sit on a
   *  silently-empty felt with no exit cue. */
  table_drained: boolean;
  /** Persistent seat→player membership map (wire minor 31), keyed by
   *  seat index. UNLIKE `seats` (hand-scoped — wiped on `hand: None`
   *  and rebuilt from every fresh-hand frame), `roster` is
   *  MEMBERSHIP-scoped and SURVIVES the inter-hand pause — that is
   *  the whole point of the field: it is the persistent
   *  WS-sourced answer to "who is sitting at this table right now",
   *  carried across `Snapshot { hand: null }` so the felt does not
   *  blank its avatars during the pause.
   *
   *  Single-writer contract — ONLY the WS reducers touch it:
   *    - `applyHandStarted` / `applySnapshot { hand: Some }` /
   *      `applySpectatorHandStarted` / `applySpectatorSnapshot {
   *      hand: Some }` REBUILD it from the per-seat `owner` fields.
   *    - `applySnapshot { hand: null }` / `applySpectatorSnapshot {
   *      hand: null }` REBUILD it from the top-level `occupied_seats`
   *      roster (the load-bearing reconnect-into-pause path).
   *    - `applyPlayerJoined` ADDs the joined seat's row.
   *    - `applyPlayerLeft` / `applyPlayerKicked` DELETE the row.
   *  No other code path writes it.
   *
   *  This is the SOLE seat-roster source for the felt + every
   *  label / award / announce consumer in `Game.tsx`. The prior
   *  parallel HTTP-hydrated `seatOwners` map + the `seatRingRows`
   *  / `dedupeSeatsByPlayer` helpers are gone; the `GET /tables/:id/
   *  state` fetch is now a one-shot-on-mount config read (max_seats /
   *  min_buy_in / last_settled_summary) only. */
  roster: ReadonlyMap<number, RosterEntry>;
}

export interface GameActions {
  applyWsEvent(ev: WsEvent): void;
  markStale(): void;
  markDisconnected(): void;
  markReconnected(): void;
  setMaxSeats(max_seats: number): void;
  /** Mirror the server-enforced table buy-in floor into the store.
   *  Game.tsx calls this from its `GET /tables/:id/state` seat-owner
   *  refetch effect (the WS `Snapshot` frame omits the field — it
   *  rides the REST state endpoint only, wire minor 25). The in-game
   *  empty-seat-tap bounds read it via `selectMinBuyIn` so the
   *  seat-in slider floor + default match the lobby join modal and
   *  the BE `buy_in_below_minimum` gate. */
  setMinBuyIn(min_buy_in: number): void;
  /** Mirror the table's maximum buy-in into the store. Same one-shot
   *  `GET /tables/:id/state` caller as `setMinBuyIn` — the two halves
   *  of the band arrive together and are now both kept. */
  setMaxBuyIn(max_buy_in: number): void;
  /** Mirror the table's invite code into the store so the in-game
   *  HUD can surface it. Same one-shot `GET /tables/:id/state`
   *  caller as `setMaxSeats` / `setMinBuyIn`. */
  setInviteCode(invite_code: string): void;
  /** Mirror the table's blinds from the same one-shot read.
   *
   *  The blinds are table-static — a hand frame carries them, but a
   *  table that has not dealt one has none, and that is exactly the
   *  table a player has just walked into. Until this, a fresh room
   *  printed its stakes as "0/0" and wore the bottom rung's felt. A
   *  hand frame overwrites with the same numbers; they cannot
   *  disagree. */
  setBlinds(blinds: { readonly small: number; readonly big: number }): void;
  /** Store the fresh per-caller legal surface returned by the
   *  `POST /action` response (`resp.legal`), or clear it to `null`
   *  (BE-authoritative legal actions stage 3). The submit caller in
   *  Game.tsx calls this with the parsed `submitAction` return so the
   *  action bar refreshes against the engine's exact bounds on a re-act
   *  / heads-up bounce-back without waiting for a reconnect Snapshot.
   *  Defensive: stores only when the surface names the caller's own seat
   *  AND the clock currently sits on the caller, else null. */
  setLegalActions(
    legal: LegalActionSetView | null,
    mySeat: number | null,
  ): void;
  /** Speculative-bump for an outgoing action submit — returns the
   *  new (already-bumped) seq the caller embeds into the envelope. */
  bumpActionSeq(): bigint;
  /** Roll the speculative bump back by 1 — invoke after a failed
   *  submit so the next attempt reuses the same seq. */
  rollbackActionSeq(): void;
  /** Reset to 0 on (re)join — mirrors the server-side
   *  `reset_player` on idempotent re-join, so the next first
   *  envelope (action_seq=1) passes the strict-monotonic gate. */
  /** Adopt the server's expected next action sequence, as carried by a
   *  consumed-sequence reject. The client owns no opinion about this
   *  number once the server has refused one. */
  syncActionSeq(serverNextSeq: bigint): void;
  resetActionSeq(): void;
  /** Clear the prior-hand surface (settled flag, awards, board,
   *  pot, round_bet, final_seed + my_hole_cards, current_to_act)
   *  and bump `next_hand_trigger` so the App-level seed-orchestrator
   *  effect re-fires. Called from `App.tsx::onNextHand` AFTER the
   *  host `/tables/:id/next-hand` POST succeeds. Preserves the
   *  carry-across-hands fields the next `HandStarted` will replace
   *  on its own (hand_seq, button, blinds, seats, hand_start_stacks,
   *  max_seats, spectators). */
  resetForNextHand(): void;
  /** Transition the showdown phase back to `settled` after the
   *  App-level reveal dwell timer expires. No-op when the current
   *  phase is anything other than `showdown` so a late call after
   *  the host already advanced to the next hand cannot revert the
   *  table to `settled` mid-lobby. */
  endShowdownDwell(): void;
  /** Fade the transient "시간 초과" badge — called by an App-
   *  level setTimeout 1.5 s after `last_timer_expired` was
   *  stamped. Idempotent: a no-op when the field is already
   *  `null` so multiple fade-out timers chaining wouldn't
   *  bounce later legitimate expiries. */
  clearLastTimerExpired(): void;
  /** Fade the transient host-ownership-transfer toast — called
   *  by an App-level setTimeout a few seconds after
   *  `last_host_transfer` was stamped. `host_seat` +
   *  `host_transfer_seq` are NOT touched (table-lifetime); only
   *  the transient banner-trigger field clears. */
  clearLastHostTransfer(): void;
  /** Stamp an HTTP-terminal seed failure the overlay selector folds
   *  into its error branch. The seed orchestrator calls this on a
   *  terminal contribute failure (5xx / commit_mismatch /
   *  unknown_player) — the ONLY write the orchestrator makes into the
   *  game store, and a SETTER (never a phase write). `hand_seq` is
   *  the round's `nextHandSeq` so the selector gates the error to the
   *  upcoming hand; a fresh-hand boundary clears it. */
  stampSeedLocalError(hand_seq: number, message: string): void;
  reset(): void;
}

export const EMPTY_BOARD: ReadonlyArray<CardView> = [];
export const EMPTY_AWARDS: ReadonlyArray<AwardView> = [];
export const EMPTY_POT_TIERS: ReadonlyArray<PotTier> = [];
export const EMPTY_REVEALS: ReadonlyMap<number, ShowdownReveal> = new Map();
export const EMPTY_SIT_OUT_STATUS: ReadonlyMap<
  number,
  {
    readonly queued: boolean;
    readonly flipped_at_ms: number;
    readonly sit_out: boolean;
  }
> = new Map();

/** Per-variant reducer signature. The mapped type fans each
 *  `WsEventKind` (re-exported from `types/wire.ts`, single source
 *  of truth) out to its `Extract<WsEvent, { kind: K }>` payload so
 *  each reducer sees a fully narrowed event without an `as any`
 *  cast at the call site. `mySeat` (the caller's own seat) and
 *  `mode` (the caller's membership lane) are BOTH derived IN-SET
 *  from the auth slice at dispatch time (`deriveMySeat` /
 *  `deriveMode`) and threaded uniformly to every arm — the
 *  self-kick + reconnect hole-card derives read `mySeat`, the
 *  spectator-strip readers read `mode`, and both replace lagging
 *  game-slice mirrors; every other arm ignores the extra args. */
export type WsEventReducerMap = {
  readonly [K in WsEventKind]: (
    state: GameState,
    ev: Extract<WsEvent, { kind: K }>,
    mySeat: number | null,
    mode: Mode,
  ) => GameState;
};

/**
 * Build a `LastHandSnapshot` from the live state. Called by the
 * next-hand reducers immediately before they clear the
 * carryable fields so the just-completed hand can be revisited.
 *
 * Returns `null` when the hand wasn't actually settled (e.g. a
 * spectator re-attach mid-hand triggering a next-hand path) so
 * the overlay's "no data yet" branch doesn't surface a
 * half-baked projection. The wall-clock stamp is computed via
 * `Date.now()` so reloads + clock skew don't poison the field
 * (no monotonic invariant — purely cosmetic for the "방금 핸드"
 * sub-label).
 */
/** Union of two per-seat maps: every key from either, the FRESH value
 *  where both have one. Used to re-read a hand's record without
 *  losing the seats the live table has since forgotten. */
function mergeKeepingPrior<V>(
  prior: ReadonlyMap<number, V>,
  fresh: ReadonlyMap<number, V>,
): ReadonlyMap<number, V> {
  const out = new Map(prior);
  for (const [k, v] of fresh) out.set(k, v);
  return out;
}

export function captureLastHandSnapshot(
  state: GameState,
): LastHandSnapshot | null {
  if (!state.settled) return null;
  // A hand's record only ever GAINS detail.
  //
  // This is taken more than once for the same hand on purpose: the
  // settle lands first and a `HandShowdown` may follow with the
  // reveals. But a later reading is taken off the live table, and the
  // live table forgets — a player who takes the pot and stands up
  // (the ordinary bot exit, and a common human one) is gone from
  // `seats` by the second reading, so a straight replace dropped the
  // hand's own winner from its record. Merging keeps whoever was in
  // the hand while still letting late reveals arrive.
  const prior =
    state.last_hand_snapshot?.hand_seq === state.hand_seq
      ? state.last_hand_snapshot
      : null;
  const seats =
    prior === null ? state.seats : mergeKeepingPrior(prior.seats, state.seats);
  const reveals =
    prior === null
      ? state.showdown_reveals
      : mergeKeepingPrior(prior.showdown_reveals, state.showdown_reveals);
  return {
    hand_seq: state.hand_seq,
    board: state.board,
    pot: state.pot,
    rake_amount: state.last_rake_amount,
    pot_tiers: state.last_pot_tiers,
    awards: state.last_awards,
    action_log: state.action_log,
    showdown_reveals: reveals,
    seats,
    dealt_seats:
      prior !== null && prior.dealt_seats.length > 0
        ? prior.dealt_seats
        : state.dealt_seats,
    my_hole_cards: state.my_hole_cards,
    captured_at_ms: Date.now(),
  };
}

export function buildInitialState(): GameState {
  return {
    hand_id: 0,
    hand_seq: 0,
    button: 0,
    blinds_small: 0,
    blinds_big: 0,
    pot: 0,
    round_bet: 0,
    min_raise: 0,
    stage: "pre_flop",
    board: EMPTY_BOARD,
    seats: new Map(),
    current_to_act: null,
    actions_taken: 0,
    settled: false,
    last_awards: EMPTY_AWARDS,
    last_winners: null,
    last_pot_tiers: EMPTY_POT_TIERS,
    last_rake_amount: 0,
    action_log: EMPTY_ACTION_LOG,
    waiting: true,
    max_seats: 0,
    min_buy_in: 0,
    max_buy_in: 0,
    invite_code: "",
    legal_actions: null,
    my_hole_cards: null,
    hand_start_stacks: new Map(),
    dealt_seats: [],
    my_action_seq: 0n,
    spectators: new Map(),
    next_hand_trigger: 0,
    player_joined_seq: 0,
    player_left_seq: 0,
    lagged_seq: 0,
    active_timer: null,
    last_timer_expired: null,
    showdown_reveals: EMPTY_REVEALS,
    mucked_seats: [],
    host_seat: null,
    host_transfer_seq: 0,
    last_host_transfer: null,
    seed_in_flight: null,
    last_seed_finalized_seq: null,
    last_seed_round_aborted: null,
    seed_round_pending: false,
    last_seed_local_error: null,
    last_table_dissolved: null,
    last_player_kicked: null,
    last_player_left: null,
    seat_sit_out_status: EMPTY_SIT_OUT_STATUS,
    last_hand_snapshot: null,
    table_state: TABLE_STATE_LOBBY,
    phase: deriveLegacyPhase(TABLE_STATE_LOBBY),
    connection: {
      ws_connected: false,
      last_event_ms: null,
      disconnect: DISCONNECT_LIVE,
    },
    surface_retry_seq: 0,
    last_hand_error: null,
    table_drained: false,
    roster: new Map(),
  };
}

/**
 * What a seat in the RUNNING hand can still do.
 *
 * `SeatStateView` has four members and the client asked about them by
 * listing members: `!== "sitting_out"` in two places, `!== "folded" &&
 * !== "sitting_out"` in three, `=== "active" || === "all_in"` in one.
 * Every one of those spellings is a different question wearing the same
 * clothes, and nothing tied a site to the question it meant to ask. So
 * when `all_in` joined the enum, each `!== "sitting_out"` site absorbed
 * it silently — no error, no test — and the pre-action bar began
 * offering 체크·폴드 / 얼마든 콜 to a player who was all-in and had no
 * decision left to reserve.
 *
 * These mirror `arkhe-holdem-engine/src/seat.rs` (`Seat::can_act`,
 * `Seat::reaches_showdown`) clause for clause, the same way
 * `nextHandBench` below mirrors the service's `start_eligible`. The
 * server is the authority on what a seat may do; the client's job is to
 * ask the same question, not to re-derive an answer.
 *
 * The switches are exhaustive against a `never` guard, so a fifth seat
 * state stops the build here — at the three answers — instead of
 * joining whichever bucket each call site happened to spell.
 */

/** Was this seat dealt into the hand now running? */
export function isDealtIn(state: SeatStateView): boolean {
  switch (state) {
    case "active":
    case "folded":
    case "all_in":
      return true;
    case "sitting_out":
      return false;
    default: {
      const exhaustive: never = state;
      return exhaustive;
    }
  }
}

/** Is this seat still contesting the pot — i.e. does it reach a
 *  showdown? Mirrors `Seat::reaches_showdown`. */
export function reachesShowdown(state: SeatStateView): boolean {
  switch (state) {
    case "active":
    case "all_in":
      return true;
    case "folded":
    case "sitting_out":
      return false;
    default: {
      const exhaustive: never = state;
      return exhaustive;
    }
  }
}

/**
 * Can this seat still be ASKED to act in the running hand?
 *
 * Mirrors `Seat::can_act`: active AND holding chips. All-in and folded
 * seats cannot, which is the whole point — and the stack clause matters
 * on its own, because a roster-derived placeholder row carries
 * `state: "active"` with `stack: 0` until the deal overwrites it.
 */
export function canAct(seat: {
  readonly state: SeatStateView;
  readonly stack: number;
}): boolean {
  return seat.state === "active" && seat.stack > 0;
}

/**
 * Is this state scoped to the hand, and therefore cleared at settle?
 *
 * `active` and `sitting_out` outlive a hand; the other two do not.
 */
export function isHandScoped(state: SeatStateView): boolean {
  switch (state) {
    case "folded":
    case "all_in":
      return true;
    case "active":
    case "sitting_out":
      return false;
    default: {
      const exhaustive: never = state;
      return exhaustive;
    }
  }
}

/**
 * Is a hand RUNNING right now — the sense the server refuses
 * table-stakes mutations in?
 *
 * Mirrors the actor's own clause, `phase.hand().is_some_and(|h|
 * !h.settled())`: a dealt-but-unsettled hand is live, and a settled one
 * (the `Settling` drain the client renders as `settled`) is not, which
 * is why a top-up lands between hands.
 *
 * Spelled `phase === "hand_active" || phase === "showdown"` at three
 * call sites and nowhere as a fact, so nothing connected the felt's
 * answer to the server's. Adding a phase means answering here.
 */
export function handIsLive(phase: Phase): boolean {
  switch (phase) {
    case "hand_active":
    case "showdown":
      return true;
    case "lobby":
    case "settled":
      return false;
    // Terminal client-side phases: the caller no longer holds a chair,
    // so there is no hand of theirs to be in.
    case "dissolved":
    case "kicked":
    case "seat_reclaimed":
      return false;
    default: {
      const exhaustive: never = phase;
      return exhaustive;
    }
  }
}

/**
 * Is the caller one of the players this seed round is asking?
 *
 * A round seals its roster into the published commit at the moment it
 * opens, and the server accepts a contribution from nobody else — it
 * answers anyone outside the roster with `403 seed.unknown_player`.
 * That refusal is correct, and it is also the last possible moment to
 * find out.
 *
 * Nothing on the client owned the question before this, so the
 * orchestrator answered it from the nearest thing it had: whether it
 * held a seat. A seat is not the same fact. A player who sits down
 * while a round is open is deliberately NOT in it — the server parks
 * them `SittingOut` precisely so the deal matches the commit — and
 * they are queued for the hand AFTER the one being shuffled. So the
 * client sent an envelope into a round it was never in, and rendered
 * the correct refusal as a broken shuffle demanding a page reload.
 *
 * `nextHandBench` cannot answer it either: that one is about the next
 * hand this seat plays, which for an arriving seat is a different hand
 * from the one this round is seeding. Only the roster knows.
 */
export type SeedRole =
  /** The round is asking this player for a number. */
  | "contributor"
  /** The round sealed without them; they watch this hand and join the
   *  next. Sending anyway earns a `403`. */
  | "bystander";

/**
 * The caller's part in a round, read off the roster the round's own
 * commit attests.
 *
 * An absent or empty roster is "this server did not say", never
 * "nobody was asked" — a sealed round binds at least two players, so
 * the empty list is unreachable from a server that carries the field.
 * Pre-bump servers omit it, and the honest fallback is the behaviour
 * the client had before the field existed: assume the round is asking.
 */
export function seedRole(
  round: { readonly expected_player_ids?: ReadonlyArray<string> },
  playerIdHex: string,
): SeedRole {
  const roster = round.expected_player_ids;
  if (roster === undefined || roster.length === 0) return "contributor";
  return roster.includes(playerIdHex) ? "contributor" : "bystander";
}

/**
 * What a seat may add to its stack, right now.
 *
 * Mirrors `TableActor::top_up_limit` (arkhe-holdem-service
 * `seat_lifecycle`) case for case, the way `nextHandBench` below
 * mirrors `start_eligible`. The server is the authority and refuses
 * anything past it; this exists so the client can OFFER the right
 * amount instead of drawing a control that is going to be refused.
 *
 * Six places were answering this question differently. The dialog did
 * not know a ceiling existed and offered fixed 100 / 500 / 1,000 /
 * 2,000 presets at every stake, so at 1/2 — where the maximum buy-in
 * is 200 — three of the four buttons could only fail. The seat menu
 * answered the "when" half from a separate `handInProgress` flag while
 * nothing answered the "how much" half at all.
 *
 * They are one question: a seat in a live hand may add nothing, which
 * is what `hand_in_progress` says. One answer drives the control's
 * enabled state, its label, and its amounts.
 */
export type TopUpLimit =
  /** A hand is live. Table stakes — chips join a stack between hands. */
  | { readonly kind: "hand_in_progress" }
  /** Already holding the table's maximum buy-in. */
  | { readonly kind: "at_ceiling" }
  /** No frame has told this client the seat's stack yet — right after a
   *  reconnect, before the snapshot lands. Not zero: a seat read as empty
   *  looks busted, and the auto top-up would ask for a full reload. */
  | { readonly kind: "stack_unknown" }
  /** May add up to `max` chips. Always positive. */
  | { readonly kind: "up_to"; readonly max: number };

export function topUpLimit(seat: {
  readonly phase: Phase;
  /** `null` until a frame has carried it. */
  readonly stack: number | null;
  /** The table's maximum buy-in, as served on the table state
   *  (`max_buy_in`). Falling back to a client-side re-derivation of the
   *  100-big-blind rule is the lobby's job, not this one's — by the
   *  time a seat is held, the served value is in hand. */
  readonly maxBuyIn: number;
  /** Whether this seat was dealt into the hand now running. Table
   *  stakes protects a stack that is in the live pot; a seat left out
   *  of the hand — out of chips, or arrived mid-hand — reloads at once,
   *  as it does on the server (`TableActor::top_up_limit`). */
  readonly dealtIn: boolean;
}): TopUpLimit {
  if (handIsLive(seat.phase) && seat.dealtIn) {
    return { kind: "hand_in_progress" };
  }
  if (seat.stack === null) return { kind: "stack_unknown" };
  const headroom = seat.maxBuyIn - seat.stack;
  if (headroom <= 0) return { kind: "at_ceiling" };
  return { kind: "up_to", max: headroom };
}

/** Chips addable right now — `0` for both refusing cases. */
export function addableNow(limit: TopUpLimit): number {
  return limit.kind === "up_to" ? limit.max : 0;
}

/** Why a seat is not being dealt the next hand. */
export type SeatBench = "no_chips" | "sitting_out";

/** The seat facts the eligibility question reads. A live `SeatRow`
 *  satisfies it, and so does a roster-derived ring row. */
export interface SeatEligibility {
  readonly state: SeatRow["state"];
  readonly stack: number;
  /** A flip the server has parked until the hand boundary
   *  (`pending_sit_out`): `true` sits this seat out from the next hand,
   *  `false` sits it back in — what a mid-hand arrival carries.
   *  `undefined` is "nothing queued", and the seat's own state
   *  answers. */
  readonly sitOutQueued?: boolean | undefined;
}

/**
 * Why this seat will not be dealt into the next hand — `null` when it
 * will be.
 *
 * Mirrors the server's `start_eligible` (arkhe-holdem-service
 * `table_actor::start_eligible`) clause for clause: the stack against
 * the big blind first, then a queued flip, then the seat's own state.
 *
 * One function because the client asked this in four places and
 * answered it three ways. The felt badge demanded the sit-out flag
 * before it would mention chips, so a player who had just spent their
 * last one was labelled nothing — while the settled bar and the
 * shuffle overlay beside it, reading the stack, both said 칩이 없어요
 * about the same seat.
 *
 * `bigBlind` of `0` is "not known yet" (pre-hydration) and never
 * disqualifies anyone.
 */
export function nextHandBench(
  seat: SeatEligibility,
  bigBlind: number,
): SeatBench | null {
  if (bigBlind > 0 && seat.stack < bigBlind) return "no_chips";
  if (seat.sitOutQueued !== undefined) {
    return seat.sitOutQueued ? "sitting_out" : null;
  }
  return seat.state === "sitting_out" ? "sitting_out" : null;
}

/**
 * Can this seat's owner lift its own bench, just by asking to be dealt
 * back in?
 *
 * Only one bench answers yes. A seat with no chips needs chips, not a
 * request; a seat with a flip already parked is on its way in or out
 * and has nothing to ask for. Offering a rejoin control on either
 * draws a button that cannot do what it says.
 */
export function canSitBackIn(seat: SeatEligibility, bigBlind: number): boolean {
  if (seat.sitOutQueued !== undefined) return false;
  return nextHandBench(seat, bigBlind) === "sitting_out";
}

/**
 * A flip the server is holding until the hand boundary — which
 * direction it goes in.
 *
 * Both directions are queued the same way: a round in progress owns a
 * frozen roster, so a seat asking to step away and a seat asking to be
 * dealt back in both wait for the same boundary. Carrying the fact as
 * one boolean — "is a sit-out queued" — was how the second direction
 * disappeared: a player who pressed "다시 참여" mid-hand saw their
 * plate say 자리비움 with the button still on it, because `false` and
 * "nothing queued" had the same shape.
 */
export type ParkedFlip = "sit_out" | "sit_in";

/** The flip the server has parked for this seat, or `null`. */
export function parkedFlipFor(
  game: { readonly seat_sit_out_status: GameState["seat_sit_out_status"] },
  seatIdx: number,
): ParkedFlip | null {
  const parked = game.seat_sit_out_status.get(seatIdx);
  if (parked?.queued !== true) return null;
  return parked.sit_out ? "sit_out" : "sit_in";
}

/**
 * The seat as the eligibility question needs to read it — the row plus
 * whatever flip the server has parked for that seat.
 *
 * The parked flip is an ARGUMENT rather than something read from the
 * store here, because the two surfaces that ask are not alike: the
 * store-side callers hold the queue map, and a theme slot holds only
 * the props it was handed. Building the shape in one place is what
 * keeps them answering the same question — the slot used to assemble
 * its own, off a boolean that could not say "sitting back in".
 */
export function seatEligibility(
  row: {
    readonly state: SeatRow["state"];
    readonly stack: number;
  },
  parked: ParkedFlip | null | undefined,
): SeatEligibility {
  return {
    state: row.state,
    stack: row.stack,
    ...(parked === null || parked === undefined
      ? {}
      : { sitOutQueued: parked === "sit_out" }),
  };
}

/**
 * Void every per-seat fact that was about the PERSON in the seat.
 *
 * A seat is a place; the facts the felt keeps per seat are mostly
 * about whoever is standing in it — the cards they showed, the action
 * they took, the flip the server parked for them. When the occupant
 * changes those facts do not transfer, and they were transferring: a
 * bot that took a seat vacated mid-hand was drawn wearing the departed
 * player's revealed hole cards, their hand rank, and their last
 * action, while its own badge said it was waiting for the next hand.
 *
 * Called from BOTH sides of an occupancy change — the leave and the
 * arrival — because either can be the one this client sees first, and
 * a client that missed the leave frame still gets a clean seat when
 * the arrival lands.
 *
 * The seat's own current-hand action-log entries go too. They read
 * "seat 3 folded", which stops being a true sentence about the seat's
 * occupant the moment the occupant changes — and it is those entries
 * the last-action chip is derived from.
 */
export function clearSeatOccupant(
  state: GameState,
  seat: number,
): Pick<
  GameState,
  | "showdown_reveals"
  | "mucked_seats"
  | "seat_sit_out_status"
  | "hand_start_stacks"
  | "action_log"
> {
  const reveals = new Map(state.showdown_reveals);
  reveals.delete(seat);
  const parked = new Map(state.seat_sit_out_status);
  parked.delete(seat);
  const stacks = new Map(state.hand_start_stacks);
  stacks.delete(seat);
  return {
    showdown_reveals: reveals.size === 0 ? EMPTY_REVEALS : reveals,
    mucked_seats: state.mucked_seats.filter((s) => s !== seat),
    seat_sit_out_status: parked.size === 0 ? EMPTY_SIT_OUT_STATUS : parked,
    hand_start_stacks: stacks,
    action_log: state.action_log.filter(
      (e) => e.seat !== seat || e.hand_seq !== state.hand_seq,
    ),
  };
}

/**
 * A row of the seat ring, plus whether its per-hand fields mean
 * anything.
 *
 * The ring is built from one of two sources and they do not know the
 * same things. The hand-scoped seat map knows what each seat is doing
 * in the hand — folded, all-in, still in. The roster knows who is
 * sitting where and nothing more, because between hands there is no
 * hand for a seat to be doing anything in.
 *
 * The roster path used to answer anyway, reporting every occupant as
 * `active`. A player who had FOLDED therefore came back to life the
 * moment the hand ended: the felt stopped dimming them and drew them a
 * fresh pair of face-down cards, because "active" is a state that
 * reaches showdown. The same invention had already been caught once
 * for sitting-out seats and fixed only for those.
 *
 * So the row says which source it came from, and the surfaces that
 * make claims about a HAND — the hole cards, the folded dimming — stay
 * quiet when the answer is not known.
 */
export interface RingRow extends SeatRow {
  readonly handStateKnown: boolean;
}

/**
 * The seat ring as the client can best know it, from whichever source
 * actually knows it.
 *
 * The hand-scoped `seats` map is a per-HAND projection that the
 * `Snapshot { hand: null }` reducer wipes — a fresh table, any
 * inter-hand pause, a table with everyone sitting out. The persistent
 * `roster` carries every occupied seat regardless of hand state, so it
 * is the answer in that window. Never a mix of the two: a mid-hand
 * leave shrinks the live map, and merging would resurrect the seat
 * that just left.
 *
 * ONE function because the felt and the shuffle overlay were reading
 * two different answers to this question. The felt fell back to the
 * roster; the overlay read the empty map, concluded nothing was known,
 * and told a benched player the shuffle was about to start.
 */
export function seatRingRows(game: {
  readonly seats: ReadonlyMap<number, SeatRow>;
  readonly roster: ReadonlyMap<number, RosterEntry>;
}): RingRow[] {
  if (game.seats.size > 0) {
    return Array.from(game.seats.values())
      .sort((a, b) => a.idx - b.idx)
      .map((row) => ({ ...row, handStateKnown: true }));
  }
  return [...game.roster.keys()]
    .sort((a, b) => a - b)
    .map((idx) => {
      const row = game.roster.get(idx);
      return {
        idx,
        // The roster's own state, not a fabricated "active" (wire minor
        // 43). A hard-coded active drew a benched player as playing, so
        // the one person who needed to be told they were sitting out
        // was shown a table about to deal a hand it could not.
        state:
          row?.sittingOut === true
            ? ("sitting_out" as const)
            : ("active" as const),
        // The roster's own stack, not a fabricated zero (wire minor
        // 40). Between hands this map IS the felt's seat source, so a
        // zero here was every seat reading as broke until the first
        // card came out.
        stack: row?.stack ?? 0,
        committed_round: 0,
        committed_hand: 0,
        acted: false,
        // The roster knows WHO is sitting there and nothing else. What
        // the seat did in a hand is not a question it can answer, and
        // the `state` above is the closest truthful reading of
        // occupancy — not a report on a hand.
        handStateKnown: false,
        ...(row?.waitingFirstHand === true ? { waitingFirstHand: true } : {}),
      };
    });
}

/** What the felt draws per seat: the rows, who sits in them, and the
 *  cards they showed. */
export interface FeltSeats {
  readonly rows: ReadonlyArray<RingRow>;
  readonly roster: ReadonlyMap<number, RosterEntry>;
  readonly reveals: ReadonlyMap<number, ShowdownReveal>;
}

/**
 * Keep a settled hand's players on the felt for as long as its result
 * is on the felt.
 *
 * Leaving is immediate, and it should be: the chair is free the moment
 * the hand is over. But the felt is still showing THAT hand — its
 * board, its pot, its showdown — and every surface that says who won
 * finds the winner by seat. A player who took the pot and stood up in
 * the same instant (a bot's ordinary exit, a human's hit-and-run) left
 * an empty chair where the winner should be: no crown, no chips gliding
 * to them, no cards, the pot stranded in the middle.
 *
 * So the hand's own record, taken at the settle, supplies whoever was
 * in the hand and has since gone. Bounded by the record: the next deal
 * replaces it and they are gone with it. The live table still wins a
 * chair it has something to say about — a newcomer who already sat in
 * it is drawn, not the ghost of the hand before.
 *
 * Returns `live` itself when nobody left, so memoised readers keep
 * their identity.
 */
export function keepSettledParticipants(
  live: FeltSeats,
  record: LastHandSnapshot | null,
): FeltSeats {
  if (record === null) return live;
  const taken = new Set<number>(live.roster.keys());
  for (const row of live.rows) taken.add(row.idx);
  const rows = [...live.rows];
  const roster = new Map(live.roster);
  const reveals = new Map(live.reveals);
  let kept = false;
  for (const [idx, row] of record.seats) {
    if (taken.has(idx) || row.ownerHex === undefined) continue;
    if (row.state === "sitting_out") continue;
    kept = true;
    rows.push({ ...row, handStateKnown: true });
    roster.set(idx, {
      playerIdHex: row.ownerHex,
      username: row.ownerName ?? null,
      stack: row.stack,
      sittingOut: false,
      waitingFirstHand: false,
    });
    const shown = record.showdown_reveals.get(idx);
    if (shown !== undefined && !reveals.has(idx)) reveals.set(idx, shown);
  }
  if (!kept) return live;
  rows.sort((a, b) => a.idx - b.idx);
  return { rows, roster, reveals };
}

/** Rebuild the persistent `roster` map from a live-hand frame's
 *  per-seat `owner` fields (`HandStarted` / `Snapshot { hand: Some }`
 *  and the spectator-lane mirrors). A seat whose wire `owner` is
 *  absent (pre-bump server) is skipped — that seat renders via the
 *  short-hex fallback until a frame carries its owner. */
export function buildRosterFromSeatOwners(
  seats: ReadonlyArray<{
    readonly idx: number;
    readonly stack: number;
    readonly owner?: SeatOwnerView | null;
  }>,
): ReadonlyMap<number, RosterEntry> {
  const out = new Map<number, RosterEntry>();
  for (const s of seats) {
    if (s.owner) {
      out.set(s.idx, {
        playerIdHex: s.owner.player_id,
        username: s.owner.username,
        stack: s.stack,
        // A live hand carries seat state on the seat rows themselves,
        // and the ring reads it from there — this map is consulted for
        // state only between hands, where the frame below builds it.
        sittingOut: false,
        waitingFirstHand: false,
      });
    }
  }
  return out;
}

/** Rebuild the persistent `roster` map from a `hand: null` reconnect
 *  frame's top-level `occupied_seats` roster — the load-bearing
 *  inter-hand-pause path (the per-seat `owner` fields are absent
 *  without a live `HandSnapshot`). Treats a missing field as an
 *  empty roster so a pre-bump server's snapshot still parses. */
export function buildRosterFromOccupiedSeats(
  occupied: ReadonlyArray<SeatOwnerSlot> | undefined,
): ReadonlyMap<number, RosterEntry> {
  const out = new Map<number, RosterEntry>();
  if (occupied === undefined) return out;
  for (const slot of occupied) {
    out.set(slot.seat, {
      playerIdHex: slot.player_id,
      username: slot.username,
      stack: slot.stack ?? 0,
      sittingOut: slot.sitting_out === true,
      waitingFirstHand: slot.waiting_first_hand === true,
    });
  }
  return out;
}

/**
 * The queued sit-out flips a snapshot reports (wire minor 44).
 *
 * `seat_sit_out_status` is the client's copy of the server's
 * `pending_sit_out` queue, and until this field it could only be filled
 * by watching a `seat_sit_out_flipped` frame go past. An event is not
 * state: a client that reloaded after one landed had no way to learn
 * it, and a reconnect is exactly the moment it needs to — the queue is
 * the first thing `start_eligible` reads.
 *
 * The snapshot is authoritative, so this REPLACES the map: a flip the
 * server has already drained must not survive as a client-side ghost.
 * A pre-bump server sends no key at all (distinct from `null`, which is
 * a minor-44 server saying the queue is empty for that seat) and the
 * client keeps what it has rather than wiping on hearsay.
 */
export function sitOutQueueFromOccupiedSeats(
  occupied: ReadonlyArray<SeatOwnerSlot> | undefined,
  current: GameState["seat_sit_out_status"],
  nowMs: number,
): GameState["seat_sit_out_status"] {
  if (occupied === undefined) return current;
  if (!occupied.some((slot) => slot.pending_sit_out !== undefined)) {
    return current;
  }
  const out = new Map<
    number,
    {
      readonly queued: boolean;
      readonly flipped_at_ms: number;
      readonly sit_out: boolean;
    }
  >();
  for (const slot of occupied) {
    const parked = slot.pending_sit_out;
    if (parked === undefined || parked === null) continue;
    // `flipped_at_ms` is when this client learned of the flip. The
    // wire carries no timestamp for a parked one — it is queue state,
    // not an event — and the field only ever dedupes live frames.
    out.set(slot.seat, { queued: true, flipped_at_ms: nowMs, sit_out: parked });
  }
  return out.size === 0 ? EMPTY_SIT_OUT_STATUS : out;
}

/**
 * The parked-flip queue as a `HandStarted` leaves it (wire minor 54).
 *
 * `NextHandStarted` drains the client's copy because the boundary
 * drains the server's — but the boundary can park a seat again (an
 * arrival held off the small blind or the button), and no other frame
 * says so. A pre-bump frame carries no field and leaves `current`.
 */
export function sitOutQueueFromStartedSeats(
  seats: ReadonlyArray<{
    readonly idx: number;
    readonly pending_sit_out?: boolean | null;
  }>,
  current: GameState["seat_sit_out_status"],
  nowMs: number,
): GameState["seat_sit_out_status"] {
  if (!seats.some((s) => s.pending_sit_out !== undefined)) return current;
  const out = new Map<
    number,
    {
      readonly queued: boolean;
      readonly flipped_at_ms: number;
      readonly sit_out: boolean;
    }
  >();
  for (const s of seats) {
    const parked = s.pending_sit_out;
    if (parked === undefined || parked === null) continue;
    out.set(s.idx, { queued: true, flipped_at_ms: nowMs, sit_out: parked });
  }
  return out.size === 0 ? EMPTY_SIT_OUT_STATUS : out;
}

/** Convert the optional `spectators` field on a HandSnapshot /
 *  SpectatorHandSnapshot into the store's `Map<player_id, username>`
 *  shape. Treats a missing field as an empty list so a pre-v0.6
 *  server's snapshot still renders cleanly. */
export function buildSpectatorMap(
  raw: ReadonlyArray<SpectatorView> | undefined,
): ReadonlyMap<string, string | null> {
  const out = new Map<string, string | null>();
  if (raw === undefined) return out;
  for (const s of raw) {
    out.set(s.player_id, s.username);
  }
  return out;
}

/**
 * The caller's own hole cards, taken from the frame that carries them
 * (wire minor 47).
 *
 * This used to RE-DERIVE the pair from `final_seed` by re-running the
 * deal. It could, because the seed determines the whole deck — which is
 * also why it no longer arrives: one value that answers "what was
 * dealt?" for every seat at once cannot give a player their own cards
 * without giving them everyone's. The server sends each socket its own
 * pair instead, so this reads rather than computes.
 *
 * Converts the wire `{rank, suit}` pair into the 0..51 deck index every
 * renderer downstream already speaks. A malformed entry yields `null`
 * ("no cards yet") rather than a wrong card.
 */
export function myCardsFromWire(
  isSpectator: boolean,
  cards: readonly [CardView, CardView] | null | undefined,
): readonly [number, number] | null {
  if (isSpectator) return null;
  if (cards === null || cards === undefined) return null;
  const a = rankSuitToCardIndex(cards[0].rank, cards[0].suit);
  const b = rankSuitToCardIndex(cards[1].rank, cards[1].suit);
  if (a === null || b === null) return null;
  return [a, b];
}
